// src/pages/OtherInstructionDetailPage/OtherInstructionDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { V } from '../../services/obfyscatedStrings';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import { BackIcon } from '../../assets/images/BackIcon';
import { platformIcons } from '../../assets/images/platformIcons';
import { WireGuardIcon, VlessIcon } from '../../assets/images/ProtocolIcons';
import Loader from '../../widgets/Loader/Loader';
import './OtherInstructionDetailPage.scss';

interface ImageStep {
    step: number;
    title: string;
    description: string;
    imageUrl: string;
    imageAlt: string;
}

interface OtherInstructionData {
    platform: string;
    platformKey: string;
    icon: string;
    installLink?: string;
    installLinks?: { ru?: string; global?: string };
    images: ImageStep[];
}

const otherInstructionsData: Record<string, Record<string, OtherInstructionData>> = {
    linux: {
        wireguard: {
            platform: 'Linux',
            platformKey: 'linux',
            icon: platformIcons.linux,
            installLink: 'https://www.wireguard.com/install/',
            images: [
                {
                    step: 1,
                    title: 'Установка WireGuard',
                    description: 'Установите WireGuard на вашу систему Linux. Перейдите на официальный сайт WireGuard и следуйте инструкциям для вашего дистрибутива.',
                    imageUrl: require('./images/linux/wireguard/step1.png'),
                    imageAlt: 'Установка WireGuard на Linux'
                },
                {
                    step: 2,
                    title: 'Получение конфигурации',
                    description: 'Перейдите во вкладку «Устройства», выберите нужное устройство (Linux) и нажмите на него, чтобы скачать файл конфигурации.',
                    imageUrl: require('./images/linux/wireguard/step2.png'),
                    imageAlt: 'Скачивание конфигурации для Linux'
                },
                {
                    step: 3,
                    title: 'Импорт конфигурации',
                    description: 'Импортируйте ранее скачанный файл конфигурации в приложение WireGuard. Используйте команду: sudo wg-quick up /путь/к/конфигурации.conf',
                    imageUrl: require('./images/linux/wireguard/step3.png'),
                    imageAlt: 'Импорт конфигурации в WireGuard'
                },
                {
                    step: 4,
                    title: `Подключение ${V()}`,
                    description: `Нажмите на кнопку «Подключить» или выполните команду sudo wg-quick up /путь/к/конфигурации.conf. Убедитесь, что трафик идёт через ${V()}.`,
                    imageUrl: require('./images/linux/wireguard/step4.png'),
                    imageAlt: `Подключение ${V()} на Linux`
                }
            ]
        },
        vless: {
            platform: 'Linux',
            platformKey: 'linux',
            icon: platformIcons.linux,
            installLink: 'https://github.com/Happ-proxy/happ-desktop/releases/download/0.2.0_alpha/Happ.linux.x86.AppImage',
            images: [
                {
                    step: 1,
                    title: 'Скачивание приложения',
                    description: 'Скачайте пакет Happ для Linux по ссылке из инструкции. Файл имеет расширение .AppImage.',
                    imageUrl: require('./images/linux/vless/step1.png'),
                    imageAlt: 'Скачивание Happ для Linux'
                },
                {
                    step: 2,
                    title: 'Установка приложения',
                    description: 'Сделайте файл исполняемым: chmod +x Happ.linux.x86.AppImage. Затем запустите его двойным кликом или через терминал.',
                    imageUrl: require('./images/linux/vless/step2.png'),
                    imageAlt: 'Установка Happ на Linux'
                },
                {
                    step: 3,
                    title: 'Получение подписки',
                    description: 'Перейдите во вкладку «Устройства», выберите нужное устройство (Linux) и нажмите «Скопировать ключ» для VLESS.',
                    imageUrl: require('./images/linux/vless/step3.png'),
                    imageAlt: 'Копирование ключа для Linux'
                },
                {
                    step: 4,
                    title: 'Добавление подписки в Happ',
                    description: 'Запустите приложение Happ, нажмите на кнопку добавления подписки. Вставьте скопированную ссылку и нажмите «Get started».',
                    imageUrl: require('./images/linux/vless/step4.png'),
                    imageAlt: 'Добавление подписки в Happ'
                },
                {
                    step: 5,
                    title: `Подключение ${V()}`,
                    description: `Нажмите на круглую кнопку включения в центре экрана. Убедитесь, что трафик идёт через ${V()}.`,
                    imageUrl: require('./images/linux/vless/step5.png'),
                    imageAlt: `Подключение ${V()} на Linux`
                },
                {
                    step: 6,
                    title: 'Важное примечание',
                    description: 'Для режима туннель обязательно запускайте Happ через sudo: sudo ./Happ.linux.x86.AppImage',
                    imageUrl: require('./images/linux/vless/step6.png'),
                    imageAlt: 'Запуск Happ с sudo'
                }
            ]
        }
    },
    androidtv: {
        wireguard: {
            platform: 'Android TV',
            platformKey: 'androidtv',
            icon: platformIcons.android,
            installLink: 'https://play.google.com/store/apps/details?id=com.alphainventor.filemanager&hl=ru',
            images: [
                { step: 1, title: 'Открытие Play Store', description: 'Найдите и откройте Google Play Store на вашем Android TV.', imageUrl: require('./images/androidtv/wireguard/step1.png'), imageAlt: 'Play Store на Android TV' },
                { step: 2, title: 'Поиск File Manager Plus', description: 'В поиске введите "File Manager Plus" и установите данное приложение.', imageUrl: require('./images/androidtv/wireguard/step2.png'), imageAlt: 'Поиск File Manager Plus' },
                { step: 3, title: 'Установка приложения', description: 'Установите и запустите это приложение.', imageUrl: require('./images/androidtv/wireguard/step3.png'), imageAlt: 'Установка File Manager Plus' },
                { step: 4, title: 'Выбор доступа из сети', description: 'Выберите "Доступ из сети".', imageUrl: require('./images/androidtv/wireguard/step4.png'), imageAlt: 'Доступ из сети' },
                { step: 5, title: 'Отключение случайного пароля', description: 'Уберите галочку со "Случайный пароль". ВАЖНО: ваш ПК и телевизор должны быть в одной сети Wi-Fi.', imageUrl: require('./images/androidtv/wireguard/step5.png'), imageAlt: 'Отключение случайного пароля' },
                { step: 6, title: 'Открытие "Мой компьютер" на ПК', description: 'Откройте на ПК "Мой компьютер". Если такого окна нет, нажмите "Компьютер" слева вверху.', imageUrl: require('./images/androidtv/wireguard/step6.png'), imageAlt: 'Мой компьютер на ПК' },
                { step: 7, title: 'Подключение сетевого диска', description: 'Нажмите "Подключить сетевой диск".', imageUrl: require('./images/androidtv/wireguard/step7.png'), imageAlt: 'Подключение сетевого диска' },
                { step: 8, title: 'Подключение к веб-сайту', description: 'Нажмите "Подключение к веб-сайту..."', imageUrl: require('./images/androidtv/wireguard/step8.png'), imageAlt: 'Подключение к веб-сайту' },
                { step: 9, title: 'Мастер подключения (1)', description: 'Нажмите "Далее".', imageUrl: require('./images/androidtv/wireguard/step9.png'), imageAlt: 'Мастер подключения 1' },
                { step: 10, title: 'Мастер подключения (2)', description: 'Нажмите "Далее".', imageUrl: require('./images/androidtv/wireguard/step10.png'), imageAlt: 'Мастер подключения 2' },
                { step: 11, title: 'Ввод сетевого интерфейса', description: 'Введите сетевой интерфейс, который у вас отображается на телевизоре. Нажмите "Далее".', imageUrl: require('./images/androidtv/wireguard/step11.png'), imageAlt: 'Ввод сетевого интерфейса' },
                { step: 12, title: 'Ввод пользователя', description: 'Уберите галочку "Анонимный вход" и в поле "Пользователь" вводим "pc". Нажмите "Далее".', imageUrl: require('./images/androidtv/wireguard/step12.png'), imageAlt: 'Ввод пользователя' },
                { step: 13, title: 'Ввод имени подключения', description: 'Введите любое имя и нажмите "Далее".', imageUrl: require('./images/androidtv/wireguard/step13.png'), imageAlt: 'Ввод имени подключения' },
                { step: 14, title: 'Завершение настройки', description: 'Нажмите "Готово".', imageUrl: require('./images/androidtv/wireguard/step14.png'), imageAlt: 'Завершение настройки' },
                { step: 15, title: 'Ввод пароля', description: 'Открылся сетевой интерфейс, где необходимо ввести пароль. Введите пароль, который у вас стоит на телевизоре. Нажмите "Вход".', imageUrl: require('./images/androidtv/wireguard/step15.png'), imageAlt: 'Ввод пароля' },
                { step: 16, title: 'Открытие папки device', description: 'Отобразились файлы телевизора. Зайдите в папку "device".', imageUrl: require('./images/androidtv/wireguard/step16.png'), imageAlt: 'Папка device' },
                { step: 17, title: 'Открытие папки Download', description: 'Далее найдите и откройте папку "Download".', imageUrl: require('./images/androidtv/wireguard/step17.png'), imageAlt: 'Папка Download' },
                { step: 18, title: 'Скачивание AmneziaWG', description: 'Установите на ПК файлы приложения "AmneziaWG" и конфигурацию с вкладки "Устройства", выбрав нужное устройство.', imageUrl: require('./images/androidtv/wireguard/step18.png'), imageAlt: 'Скачивание AmneziaWG' },
                { step: 19, title: 'Копирование файлов', description: 'Скопируйте скачанные файлы приложения "AmneziaWG" и ключа из бота на телевизор в папку "Download".', imageUrl: require('./images/androidtv/wireguard/step19.png'), imageAlt: 'Копирование файлов' },
                { step: 20, title: 'Переход на телевизор', description: 'Перейдите на телевизор, остановите соединение. Зайдите в папку "Телефон".', imageUrl: require('./images/androidtv/wireguard/step20.png'), imageAlt: 'Переход на телевизор' },
                { step: 21, title: 'Открытие папки Download на TV', description: 'Откройте папку "Download".', imageUrl: require('./images/androidtv/wireguard/step21.png'), imageAlt: 'Папка Download на TV' },
                { step: 22, title: 'Установка AmneziaWG', description: 'Нажмите на приложение "AmneziaWG", установите и откройте.', imageUrl: require('./images/androidtv/wireguard/step22.png'), imageAlt: 'Установка AmneziaWG' },
                { step: 23, title: 'Добавление конфигурации', description: 'Нажмите на "+".', imageUrl: require('./images/androidtv/wireguard/step23.png'), imageAlt: 'Добавление конфигурации' },
                { step: 24, title: 'Импорт из файла', description: 'Нажмите "Импортировать из файла".', imageUrl: require('./images/androidtv/wireguard/step24.png'), imageAlt: 'Импорт из файла' },
                { step: 25, title: 'Выбор конфигурации', description: 'Выберите конфигурацию с нашего бота.', imageUrl: require('./images/androidtv/wireguard/step25.png'), imageAlt: 'Выбор конфигурации' },
                { step: 26, title: `Активация ${V()}`, description: 'В приложении "AmneziaWG" нажмите на кнопку активации.', imageUrl: require('./images/androidtv/wireguard/step26.png'), imageAlt: `Активация ${V()}` },
                { step: 27, title: `Разрешение подключения`, description: `Разрешите "Запрос на подключение". ${V()} подключён!`, imageUrl: require('./images/androidtv/wireguard/step27.png'), imageAlt: 'Разрешение подключения' }
            ]
        },
        vless: {
            platform: 'Android TV',
            platformKey: 'androidtv',
            icon: platformIcons.android,
            installLinks: {
                ru: 'https://play.google.com/store/apps/details?id=com.v2raytun.android',
                global: 'https://play.google.com/store/apps/details?id=com.v2raytun.android'
            },
            images: [
                {
                    step: 1,
                    title: 'Установка приложения v2RayTun',
                    description: 'Установите приложение v2RayTun из Google Play на вашем Android TV.',
                    imageUrl: require('./images/androidtv/vless/step1.png'),
                    imageAlt: 'Установка v2RayTun'
                },
                {
                    step: 2,
                    title: 'Открытие приложения',
                    description: 'Откройте установленное приложение «v2RayTun», нажмите на «Управление».',
                    imageUrl: require('./images/androidtv/vless/step2.png'),
                    imageAlt: 'Открытие v2RayTun'
                },
                {
                    step: 3,
                    title: 'Ручной ввод',
                    description: 'Нажмите на кнопку «Ручной ввод» для добавления конфигурации вручную.',
                    imageUrl: require('./images/androidtv/vless/step3.png'),
                    imageAlt: 'Ручной ввод в v2RayTun'
                },
                {
                    step: 4,
                    title: 'Получение ссылки',
                    description: 'Перейдите во вкладку «Устройства», выберите нужное устройство (Android TV) и нажмите «Скопировать ключ».',
                    imageUrl: require('./images/androidtv/vless/step4.png'),
                    imageAlt: 'Копирование ключа для Android TV'
                },
                {
                    step: 5,
                    title: 'Ввод ссылки',
                    description: 'Введите на телевизоре ссылку для ручного ввода (вставьте скопированную ссылку) и нажмите «ОК».',
                    imageUrl: require('./images/androidtv/vless/step5.png'),
                    imageAlt: 'Ввод ссылки в v2RayTun'
                },
                {
                    step: 6,
                    title: 'Проверка ссылки',
                    description: 'Убедитесь в корректности ссылки, проверьте каждый символ — это очень важно.',
                    imageUrl: require('./images/androidtv/vless/step6.png'),
                    imageAlt: 'Проверка ссылки'
                },
                {
                    step: 7,
                    title: `Подключение ${V()}`,
                    description: 'После этого, на главном экране приложения появится подписка ShadowNet. Вы увидите список доступных локаций, нажмите на синюю кнопку для включения.',
                    imageUrl: require('./images/androidtv/vless/step7.png'),
                    imageAlt: `Подключение ${V()} на Android TV`
                }
            ]
        }
    }
};

function OtherInstructionDetailPage() {
    const { platform } = useParams<{ platform: string }>();
    const [searchParams] = useSearchParams();
    const protocolParam = searchParams.get('protocol');
    const navigate = useNavigate();
    const [selectedProtocol, setSelectedProtocol] = useState<string>(protocolParam || 'wireguard');
    const [showProtocolModal, setShowProtocolModal] = useState<boolean>(!protocolParam);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }

        // ДОБАВИТЬ ЭТОТ ТАЙМЕР
        const timer = setTimeout(() => {
            setLoading(false);
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    const platformData = otherInstructionsData[platform as string];
    const instruction = platformData?.[selectedProtocol];

    const protocolIconsMap: Record<string, React.ReactNode> = {
        wireguard: <WireGuardIcon />,
        vless: <VlessIcon />,
    };

    const handleProtocolSelect = (protocol: string) => {
        setSelectedProtocol(protocol);
        setShowProtocolModal(false);
        navigate(`/other-instructions/${platform}?protocol=${protocol}`, { replace: true });
    };

    if (!platform || !platformData) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="other-instruction-page">
                    <div className="other-error-container">
                        <div className="other-error-icon">❌</div>
                        <h2>Платформа не найдена</h2>
                        <button className="other-continue-btn" onClick={() => navigate('/other-platforms')}>
                            Вернуться к выбору платформы
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!instruction) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="other-instruction-page">
                    <div className="other-error-container">
                        <div className="other-error-icon">❌</div>
                        <h2>Инструкция не найдена</h2>
                        <button className="other-continue-btn" onClick={() => setShowProtocolModal(true)}>
                            Выбрать протокол
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar />
                <div className="other-instruction-page loading">
                    <div className="other-loader"><Loader /></div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar />

            <div className="other-instruction-page">
                <div className="other-page-header">
                    <button className="other-back-btn" onClick={() => navigate('/other-platforms')}>
                        <BackIcon />
                    </button>
                    <h1 className="other-page-title">Инструкция</h1>
                </div>

                <div className="other-instruction-header">
                    <div className="other-instruction-hero">
                        <div className="other-hero-icon2">
                            <img src={instruction.icon} alt={instruction.platform} />
                        </div>
                    </div>
                    <div className="other-instruction-hero">
                        <div className="other-hero-info">
                            <button className="other-protocol-selector" onClick={() => setShowProtocolModal(true)}>
                                <span className="other-hero-badge">
                                    <span className="other-protocol-icon">
                                        {protocolIconsMap[selectedProtocol]}
                                    </span>
                                    <span className="other-protocol-text">
                                        {selectedProtocol === 'wireguard' ? 'WireGuard' : 'VLESS'}
                                    </span>
                                    <svg className="other-protocol-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M6 9l6 6 6-6" />
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="other-install-container">
                    {instruction.installLinks && (
                        <div className="other-install-links">
                            {instruction.installLinks.ru && (
                                <a href={instruction.installLinks.ru} target="_blank" rel="noopener noreferrer" className="other-download-btn">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    Скачать приложение (RU)
                                </a>
                            )}
                            {instruction.installLinks.global && (
                                <a href={instruction.installLinks.global} target="_blank" rel="noopener noreferrer" className="other-download-btn">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    Скачать приложение (Global)
                                </a>
                            )}
                        </div>
                    )}
                    {instruction.installLink && !instruction.installLinks && (
                        <a href={instruction.installLink} target="_blank" rel="noopener noreferrer" className="other-download-btn">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Скачать приложение
                        </a>
                    )}
                </div>

                <div className="other-image-steps">
                    {instruction.images.map((img) => (
                        <div key={img.step} className="other-step-card">
                            <div className="other-step-header">
                                <span className="other-step-number">{img.step}</span>
                                <h3 className="other-step-title">{img.title}</h3>
                            </div>
                            <p className="other-step-desc">{img.description}</p>
                            <div className="other-step-image">
                                <img
                                    src={img.imageUrl}
                                    alt={img.imageAlt}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                        if (placeholder) placeholder.style.display = 'flex';
                                    }}
                                />
                                <div className="other-image-placeholder" style={{ display: 'none' }}>
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="2" y="2" width="20" height="20" rx="2" />
                                        <circle cx="8.5" cy="8.5" r="2.5" />
                                        <path d="M21 15l-5-4-3 3-4-4-5 5" />
                                    </svg>
                                    <span>Изображение скоро появится</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showProtocolModal &&
                createPortal(
                    <div className="other-modal-overlay" onClick={() => setShowProtocolModal(false)}>
                        <div className="other-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="other-modal-header">
                                <h3>Выберите протокол</h3>
                                <button className="other-modal-close" onClick={() => setShowProtocolModal(false)}>✕</button>
                            </div>
                            <div className="other-modal-body">
                                {Object.entries(platformData).map(([key]) => (
                                    <button
                                        key={key}
                                        className={`other-modal-option ${selectedProtocol === key ? 'active' : ''}`}
                                        onClick={() => handleProtocolSelect(key)}
                                    >
                                        <div className="other-modal-option-left">
                                            <span className="other-modal-option-icon">
                                                {protocolIconsMap[key]}
                                            </span>
                                            <span className="other-modal-option-name">
                                                {key === 'wireguard' ? 'WireGuard' : 'VLESS'}
                                            </span>
                                        </div>
                                        <span className="other-modal-option-arrow">→</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}

export default OtherInstructionDetailPage;